import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { documentService, userService, signatureService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
  Button,
  Input,
  Badge,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Label,
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../components/ui';
import {
  Upload,
  Download,
  Trash2,
  Search,
  FileText,
  File,
  Image,
  Loader2,
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  Eye,
  PenLine,
  AlertCircle,
} from 'lucide-react';
import DocumentPreview from '../../components/documents/DocumentPreview';
import SignDocumentDialog from '../../components/documents/SignDocumentDialog';

/**
 * Pagina gestione documenti
 */
function DocumentsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
  });

  // Filtri
  const [search, setSearch] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState('all');
  const [clientFilter, setClientFilter] = useState(searchParams.get('clientId') || 'all');
  const [clients, setClients] = useState([]);

  // Dialog upload
  const [uploadDialog, setUploadDialog] = useState({ open: false });
  const [uploadData, setUploadData] = useState({
    file: null,
    title: '',
    description: '',
    clientId: '',
    requiresSignature: false,
    signatureType: 'acknowledgment',
    signatureDeadline: '',
    signatureInstructions: '',
  });
  const [isUploading, setIsUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState({});

  // Dialog conferma eliminazione
  const [deleteDialog, setDeleteDialog] = useState({ open: false, document: null });
  const [isDeleting, setIsDeleting] = useState(false);

  // Dialog preview
  const [previewDialog, setPreviewDialog] = useState({ open: false, document: null });

  // Dialog firma
  const [signDialog, setSignDialog] = useState({ open: false, document: null });

  // Stato firme documenti
  const [documentSignatures, setDocumentSignatures] = useState({});

  // Documenti in attesa di firma (per clienti)
  const [pendingSignatures, setPendingSignatures] = useState([]);

  // Carica clienti per admin/operatore
  useEffect(() => {
    if (user.role === 'admin') {
      userService.getClients().then((res) => setClients(res.data?.clients || []));
    } else if (user.role === 'operator') {
      import('../../services/api').then(({ operatorService }) => {
        operatorService.getMyClients().then((res) => setClients(res.data?.clients || []));
      });
    }
  }, [user.role]);

  useEffect(() => {
    loadDocuments();
  }, [pagination.page, fileTypeFilter, clientFilter]);

  // Debounce ricerca
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.page === 1) {
        loadDocuments();
      } else {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search]);

  const loadDocuments = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (search) params.search = search;
      if (fileTypeFilter !== 'all') params.fileType = fileTypeFilter;
      if (clientFilter !== 'all') params.clientId = clientFilter;

      const response = await documentService.getAll(params);
      setDocuments(response.data?.documents || []);
      setPagination((prev) => ({
        ...prev,
        total: response.pagination?.total || 0,
        pages: response.pagination?.pages || 0,
      }));
    } catch (error) {
      toast.error('Errore', 'Impossibile caricare i documenti');
    } finally {
      setIsLoading(false);
    }
  };

  // Carica stato firme per i documenti che richiedono firma
  const loadSignatureStatuses = async () => {
    const docsRequiringSignature = documents.filter((doc) => doc.requiresSignature);
    if (docsRequiringSignature.length === 0) return;

    const statuses = {};
    for (const doc of docsRequiringSignature) {
      try {
        const response = await signatureService.getStatus(doc._id);
        statuses[doc._id] = response.data;
      } catch {
        statuses[doc._id] = { isSigned: false };
      }
    }
    setDocumentSignatures(statuses);
  };

  // Carica stato firme quando cambiano i documenti
  useEffect(() => {
    if (documents.length > 0) {
      loadSignatureStatuses();
    }
  }, [documents]);

  // Carica documenti in attesa di firma per i clienti
  const loadPendingSignatures = async () => {
    if (user.role !== 'client') return;
    try {
      const response = await signatureService.getPending();
      setPendingSignatures(response.data?.documents || []);
    } catch (error) {
      console.error('Errore caricamento firme pendenti:', error);
    }
  };

  // Carica firme pendenti per i clienti
  useEffect(() => {
    loadPendingSignatures();
  }, [user.role]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadData((prev) => ({
        ...prev,
        file,
        title: prev.title || file.name.replace(/\.[^/.]+$/, ''),
      }));
      // Rimuovi errore file se presente
      if (uploadErrors.file) {
        setUploadErrors((prev) => ({ ...prev, file: null }));
      }
    }
  };

  const handleUpload = async () => {
    // Validazione completa con errori per campo
    const errors = {};

    if (!uploadData.file) {
      errors.file = 'Seleziona un file da caricare';
    }

    if (!uploadData.title?.trim()) {
      errors.title = 'Il titolo è obbligatorio';
    }

    // Verifica clientId per admin/operatore
    if (user.role !== 'client' && !uploadData.clientId) {
      errors.clientId = 'Seleziona il cliente destinatario';
    }

    // Validazione campi firma
    if (user.role !== 'client' && uploadData.requiresSignature) {
      if (!uploadData.signatureType) {
        errors.signatureType = 'Seleziona il tipo di firma';
      }
    }

    // Se ci sono errori, mostrali e non procedere
    if (Object.keys(errors).length > 0) {
      setUploadErrors(errors);
      return;
    }

    // Reset errori e procedi
    setUploadErrors({});
    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('document', uploadData.file);
      formData.append('title', uploadData.title.trim());
      if (uploadData.description) {
        formData.append('description', uploadData.description);
      }
      if (uploadData.clientId) {
        formData.append('clientId', uploadData.clientId);
      }
      // Campi firma (solo admin/operator)
      if (user.role !== 'client' && uploadData.requiresSignature) {
        formData.append('requiresSignature', 'true');
        formData.append('signatureType', uploadData.signatureType);
        if (uploadData.signatureDeadline) {
          formData.append('signatureDeadline', uploadData.signatureDeadline);
        }
        if (uploadData.signatureInstructions) {
          formData.append('signatureInstructions', uploadData.signatureInstructions);
        }
      }

      await documentService.upload(formData);
      toast.success('Documento caricato', 'Il documento è stato caricato con successo');
      setUploadDialog({ open: false });
      setUploadData({
        file: null,
        title: '',
        description: '',
        clientId: '',
        requiresSignature: false,
        signatureType: 'acknowledgment',
        signatureDeadline: '',
        signatureInstructions: '',
      });
      setUploadErrors({});
      loadDocuments();
    } catch (error) {
      toast.error('Errore', error.response?.data?.message || 'Impossibile caricare il documento');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await documentService.download(doc._id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Download avviato', doc.fileName);
    } catch (error) {
      toast.error('Errore', 'Impossibile scaricare il documento');
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.document) return;

    setIsDeleting(true);
    try {
      await documentService.delete(deleteDialog.document._id);
      toast.success('Documento eliminato', 'Il documento è stato eliminato');
      setDeleteDialog({ open: false, document: null });
      loadDocuments();
    } catch (error) {
      toast.error('Errore', error.response?.data?.message || 'Impossibile eliminare il documento');
    } finally {
      setIsDeleting(false);
    }
  };

  const getFileIcon = (fileType) => {
    const icons = {
      pdf: <FileText className="text-red-500" size={20} />,
      doc: <FileText className="text-blue-500" size={20} />,
      docx: <FileText className="text-blue-500" size={20} />,
      jpg: <Image className="text-green-500" size={20} />,
      jpeg: <Image className="text-green-500" size={20} />,
      png: <Image className="text-green-500" size={20} />,
      gif: <Image className="text-purple-500" size={20} />,
    };
    return icons[fileType] || <File className="text-gray-500" size={20} />;
  };

  const canDelete = (doc) => {
    if (user.role === 'admin') return true;
    return doc.uploadedBy?._id === user._id;
  };

  return (
    <div className="space-y-4 sm:space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-foreground">
            {user.role === 'client' ? 'I miei documenti' : 'Documenti'}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {pagination.total} documenti totali
          </p>
        </div>
        <Button onClick={() => setUploadDialog({ open: true })} className="w-full sm:w-auto min-h-[44px] touch-manipulation">
          <Plus className="mr-2 h-4 w-4" />
          Carica documento
        </Button>
      </div>

      {/* Documenti da firmare (solo clienti) */}
      {user.role === 'client' && pendingSignatures.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <h2 className="text-lg font-semibold text-amber-800 dark:text-amber-200">
              Documenti da firmare ({pendingSignatures.length})
            </h2>
          </div>
          <div className="space-y-2">
            {pendingSignatures.map((doc) => (
              <div
                key={doc._id}
                className="flex items-center justify-between p-3 bg-white dark:bg-card rounded-lg border border-amber-100 dark:border-amber-800/50"
              >
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{doc.title}</p>
                  <p className="text-sm text-muted-foreground">
                    {doc.signatureType === 'acknowledgment' && 'Presa visione'}
                    {doc.signatureType === 'approval' && 'Approvazione'}
                    {doc.signatureType === 'consent' && 'Consenso'}
                    {doc.signatureDeadline && (
                      <span>
                        {' '}• Scadenza: {new Date(doc.signatureDeadline).toLocaleDateString('it-IT')}
                      </span>
                    )}
                  </p>
                </div>
                <Button
                  onClick={() => setSignDialog({ open: true, document: doc })}
                  className="ml-4"
                >
                  <PenLine className="mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Firma</span>
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filtri */}
      <div className="flex flex-col gap-3 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Cerca per titolo, descrizione..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 min-h-[44px] text-base"
          />
        </div>

        <div className="flex flex-wrap gap-2 sm:gap-4">
          <Select value={fileTypeFilter} onValueChange={setFileTypeFilter}>
            <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-[130px] min-h-[44px]">
              <SelectValue placeholder="Tipo file" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti i tipi</SelectItem>
              <SelectItem value="pdf">PDF</SelectItem>
              <SelectItem value="docx">Word</SelectItem>
              <SelectItem value="jpg">JPG</SelectItem>
              <SelectItem value="png">PNG</SelectItem>
            </SelectContent>
          </Select>

          {user.role !== 'client' && clients.length > 0 && (
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-[calc(50%-0.25rem)] sm:w-[180px] min-h-[44px]">
                <SelectValue placeholder="Cliente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti i clienti</SelectItem>
                {clients.map((client) => (
                  <SelectItem key={client._id} value={client._id}>
                    {client.firstName} {client.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      {/* Tabella documenti */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-primary" size={32} />
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto text-muted-foreground mb-4" size={48} />
              <p className="text-muted-foreground">Nessun documento trovato</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Documento</TableHead>
                  <TableHead className="hidden md:table-cell">Cliente</TableHead>
                  <TableHead className="hidden lg:table-cell">Caricato da</TableHead>
                  <TableHead className="hidden sm:table-cell">Data</TableHead>
                  <TableHead className="text-right">Azioni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc._id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc.fileType)}
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="font-medium">{doc.title}</p>
                            {doc.requiresSignature && (
                              <Badge
                                variant={documentSignatures[doc._id]?.isSigned ? 'default' : 'secondary'}
                                className="text-xs"
                              >
                                {documentSignatures[doc._id]?.isSigned ? 'Firmato' : 'Da firmare'}
                              </Badge>
                            )}
                          </div>
                          {doc.description && (
                            <p className="text-sm text-muted-foreground line-clamp-1" title={doc.description}>
                              {doc.description}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {doc.fileType.toUpperCase()} • {doc.fileSizeFormatted}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      {doc.clientId && (
                        <div>
                          <p>{doc.clientId.firstName} {doc.clientId.lastName}</p>
                          {doc.clientId.guestName && (
                            <p className="text-sm text-muted-foreground">
                              {doc.clientId.guestName}
                            </p>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      {doc.uploadedBy && (
                        <div>
                          <p>{doc.uploadedBy.firstName} {doc.uploadedBy.lastName}</p>
                          <Badge variant="secondary" className="text-xs">
                            {doc.uploadedByRole}
                          </Badge>
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      {new Date(doc.createdAt).toLocaleDateString('it-IT')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1 sm:gap-2">
                        {/* Pulsante firma per clienti */}
                        {user.role === 'client' && doc.requiresSignature && !documentSignatures[doc._id]?.isSigned && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Firma"
                            onClick={() => setSignDialog({ open: true, document: doc })}
                            className="text-primary min-h-[40px] min-w-[40px] touch-manipulation"
                          >
                            <PenLine className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Anteprima"
                          onClick={() => setPreviewDialog({ open: true, document: doc })}
                          className="min-h-[40px] min-w-[40px] touch-manipulation"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Scarica"
                          onClick={() => handleDownload(doc)}
                          className="min-h-[40px] min-w-[40px] touch-manipulation"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        {canDelete(doc) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Elimina"
                            onClick={() => setDeleteDialog({ open: true, document: doc })}
                            className="min-h-[40px] min-w-[40px] touch-manipulation"
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          {/* Paginazione */}
          {pagination.pages > 1 && (
            <div className="flex items-center justify-between px-4 py-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Pagina {pagination.page} di {pagination.pages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog upload */}
      <Dialog open={uploadDialog.open} onOpenChange={(open) => {
        setUploadDialog({ open });
        if (!open) setUploadErrors({});
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Carica documento</DialogTitle>
            <DialogDescription>
              Seleziona un file da caricare (max 10MB)
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* File input */}
            <div className="space-y-2">
              <Label className={uploadErrors.file ? 'text-destructive' : ''}>File *</Label>
              {uploadData.file ? (
                <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div className="flex items-center gap-2">
                    {getFileIcon(uploadData.file.name.split('.').pop())}
                    <span className="text-sm truncate max-w-[200px]">
                      {uploadData.file.name}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setUploadData((prev) => ({ ...prev, file: null }))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-accent/50 transition-colors ${uploadErrors.file ? 'border-destructive bg-destructive/5' : 'border-border'}`}>
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className={`w-8 h-8 mb-2 ${uploadErrors.file ? 'text-destructive' : 'text-muted-foreground'}`} />
                    <p className={`text-sm ${uploadErrors.file ? 'text-destructive' : 'text-muted-foreground'}`}>
                      Clicca per selezionare
                    </p>
                    <p className="text-xs text-muted-foreground">
                      PDF, DOC, DOCX, JPG, PNG (max 10MB)
                    </p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                    onChange={handleFileSelect}
                  />
                </label>
              )}
              {uploadErrors.file && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {uploadErrors.file}
                </p>
              )}
            </div>

            {/* Titolo */}
            <div className="space-y-2">
              <Label htmlFor="title" className={uploadErrors.title ? 'text-destructive' : ''}>Titolo *</Label>
              <Input
                id="title"
                value={uploadData.title}
                onChange={(e) => {
                  setUploadData((prev) => ({ ...prev, title: e.target.value }));
                  if (uploadErrors.title) setUploadErrors((prev) => ({ ...prev, title: null }));
                }}
                placeholder="Titolo del documento"
                className={uploadErrors.title ? 'border-destructive focus:ring-destructive' : ''}
              />
              {uploadErrors.title && (
                <p className="text-sm text-destructive flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {uploadErrors.title}
                </p>
              )}
            </div>

            {/* Descrizione */}
            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <textarea
                id="description"
                value={uploadData.description}
                onChange={(e) => setUploadData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="Descrizione opzionale..."
                className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {/* Selezione cliente (admin/operatore) */}
            {user.role !== 'client' && (
              <div className="space-y-2">
                <Label className={uploadErrors.clientId ? 'text-destructive' : ''}>Cliente destinatario *</Label>
                <Select
                  value={uploadData.clientId}
                  onValueChange={(value) => {
                    setUploadData((prev) => ({ ...prev, clientId: value }));
                    if (uploadErrors.clientId) setUploadErrors((prev) => ({ ...prev, clientId: null }));
                  }}
                >
                  <SelectTrigger className={uploadErrors.clientId ? 'border-destructive focus:ring-destructive' : ''}>
                    <SelectValue placeholder="Seleziona cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client._id} value={client._id}>
                        {client.firstName} {client.lastName}
                        {client.guestName && ` (${client.guestName})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {uploadErrors.clientId && (
                  <p className="text-sm text-destructive flex items-center gap-1">
                    <AlertCircle className="h-4 w-4" />
                    {uploadErrors.clientId}
                  </p>
                )}
              </div>
            )}

            {/* Richiesta firma (solo admin/operatore) */}
            {user.role !== 'client' && (
              <div className="space-y-4 pt-4 border-t border-border">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="requiresSignature"
                    checked={uploadData.requiresSignature}
                    onChange={(e) => setUploadData((prev) => ({
                      ...prev,
                      requiresSignature: e.target.checked,
                    }))}
                    className="h-4 w-4 rounded border-input"
                  />
                  <Label htmlFor="requiresSignature" className="cursor-pointer">
                    Richiedi firma al cliente
                  </Label>
                </div>

                {uploadData.requiresSignature && (
                  <>
                    <div className="space-y-2">
                      <Label className={uploadErrors.signatureType ? 'text-destructive' : ''}>Tipo di firma *</Label>
                      <Select
                        value={uploadData.signatureType}
                        onValueChange={(value) => {
                          setUploadData((prev) => ({
                            ...prev,
                            signatureType: value,
                          }));
                          if (uploadErrors.signatureType) setUploadErrors((prev) => ({ ...prev, signatureType: null }));
                        }}
                      >
                        <SelectTrigger className={uploadErrors.signatureType ? 'border-destructive focus:ring-destructive' : ''}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="acknowledgment">Presa visione</SelectItem>
                          <SelectItem value="approval">Approvazione</SelectItem>
                          <SelectItem value="consent">Consenso</SelectItem>
                        </SelectContent>
                      </Select>
                      {uploadErrors.signatureType && (
                        <p className="text-sm text-destructive flex items-center gap-1">
                          <AlertCircle className="h-4 w-4" />
                          {uploadErrors.signatureType}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Scadenza firma (opzionale)</Label>
                      <Input
                        type="date"
                        value={uploadData.signatureDeadline}
                        onChange={(e) => setUploadData((prev) => ({
                          ...prev,
                          signatureDeadline: e.target.value,
                        }))}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Istruzioni per il firmatario (opzionale)</Label>
                      <textarea
                        value={uploadData.signatureInstructions}
                        onChange={(e) => setUploadData((prev) => ({
                          ...prev,
                          signatureInstructions: e.target.value,
                        }))}
                        placeholder="Es: Leggere attentamente prima di firmare..."
                        className="w-full min-h-[60px] px-3 py-2 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        maxLength={500}
                      />
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadDialog({ open: false })}>
              Annulla
            </Button>
            <Button onClick={handleUpload} isLoading={isUploading}>
              <Upload className="mr-2 h-4 w-4" />
              Carica
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog conferma eliminazione */}
      <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, document: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina documento</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare <strong>{deleteDialog.document?.title}</strong>?
              Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog({ open: false, document: null })}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDelete} isLoading={isDeleting}>
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview documento */}
      <DocumentPreview
        document={previewDialog.document}
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, document: null })}
      />

      {/* Dialog firma documento */}
      <SignDocumentDialog
        document={signDialog.document}
        open={signDialog.open}
        onClose={() => setSignDialog({ open: false, document: null })}
        onSigned={() => {
          loadDocuments();
          loadSignatureStatuses();
          loadPendingSignatures();
        }}
      />
    </div>
  );
}

export default DocumentsPage;
